/**
 * usePathFinding Hook
 *
 * React hook for optimal path finding between structures using GeoServer WFS.
 * Replaces the CFN.getPath function from legacy routing.js.
 *
 * Features:
 * - Fetches optimal paths between two vertices via GeoServer
 * - Supports request cancellation for rapid successive calls
 * - Exposes API on window.CFN for backward compatibility
 */
import { useCallback, useRef } from 'react';
const GEOSERVER_URL = '/geoserver';
/**
 * Hook for path finding operations
 */
export function usePathFinding() {
    const abortControllerRef = useRef(null);
    const getPath = useCallback(async (sourceVertexId, targetVertexId) => {
        // Cancel any existing request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();
        try {
            const url = `${GEOSERVER_URL}/wfs?service=WFS&version=1.0.0&request=GetFeature&typeName=cfn:optimal_path&viewparams=source:${sourceVertexId}%3Btarget:${targetVertexId}%3B&outputformat=application/json`;
            const response = await fetch(url, {
                method: 'GET',
                signal: abortControllerRef.current.signal,
            });
            if (!response.ok) {
                throw new Error(`Path finding failed: ${response.statusText}`);
            }
            const data = await response.json();
            return data;
        }
        catch (error) {
            // Don't log abort errors - they're expected
            if (error instanceof Error && error.name === 'AbortError') {
                return null;
            }
            console.error('usePathFinding: Error fetching path:', error);
            return null;
        }
    }, []);
    const cancelPath = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, []);
    return {
        getPath,
        cancelPath,
    };
}
/**
 * Standalone getPath function for use outside React components.
 * This provides backward compatibility with legacy code that calls CFN.getPath.
 */
let globalAbortController = null;
export async function getPathStandalone(sourceVertexId, targetVertexId) {
    // Cancel any existing request
    if (globalAbortController) {
        globalAbortController.abort();
    }
    globalAbortController = new AbortController();
    try {
        const url = `${GEOSERVER_URL}/wfs?service=WFS&version=1.0.0&request=GetFeature&typeName=cfn:optimal_path&viewparams=source:${sourceVertexId}%3Btarget:${targetVertexId}%3B&outputformat=application/json`;
        const response = await fetch(url, {
            method: 'GET',
            signal: globalAbortController.signal,
        });
        if (!response.ok) {
            throw new Error(`Path finding failed: ${response.statusText}`);
        }
        return await response.json();
    }
    catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            return null;
        }
        console.error('getPathStandalone: Error fetching path:', error);
        return null;
    }
}
/**
 * Legacy-compatible getPath function that uses callbacks.
 * This matches the original CFN.getPath(a, b, callback) signature.
 */
export function getPathWithCallback(sourceVertexId, targetVertexId, callback) {
    getPathStandalone(sourceVertexId, targetVertexId)
        .then((data) => {
        if (data) {
            callback(data);
        }
    })
        .catch(() => {
        // Error already logged in getPathStandalone
    });
}
/**
 * Initialize CFN.getPath for backward compatibility with legacy code.
 * Call this once during app initialization.
 */
export function initializePathFinding() {
    if (typeof window !== 'undefined') {
        const CFN = window.CFN || {};
        CFN.getPath = getPathWithCallback;
        window.CFN = CFN;
        console.log('usePathFinding: Initialized CFN.getPath');
    }
}
export default usePathFinding;
